import { GridCellProps } from '@progress/kendo-react-grid';
import { isUndefined } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import NumberInput from 'src/components/NumberInput';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { getBgColor } from 'src/utils/icons';
import { round } from 'src/utils/number';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const QuantityReceived: FC<GridCellProps> = (props: GridCellProps) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const warnings = useMemo(
    () => dataItem?.warnings?.invoicedQuantities,
    [dataItem?.warnings?.invoicedQuantities]
  );

  const errors = useMemo(
    () => dataItem?.errors?.quantityReceived,
    [dataItem?.errors?.quantityReceived]
  );

  const infos = useMemo(
    () => dataItem?.informations?.quantityReceived,
    [dataItem?.informations?.quantityReceived]
  );
  const { value, handleChange } = useDelayedInputOnchange(props);

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.itemQuantity?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);

  const isEdited = useMemo(() => {
    const totalOcrItemQuantity = matchingItem.reduce(
      (acc, item) => (acc += Number(item?.item?.itemQuantity?.value || 0)),
      0
    );
    return round(totalOcrItemQuantity, 2) !== round(Number(value), 2);
  }, [matchingItem, value]);

  const matchItemIndex = matchingItem.map(
    (item) => Number(item?.item?.itemSequenceNumber?.value) - 1
  );
  const handleHover = useCallback(() => {
    handleHoverOcrField('itemQuantity', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);

  const showOcrIndicator = useMemo(
    () =>
      initialInvoice?.ocrGenerated &&
      averageOcrConfidence !== 0 &&
      matchingItem.length > 0 &&
      Number(value) !== 0,
    [initialInvoice?.ocrGenerated, averageOcrConfidence, matchingItem.length, value]
  );

  return (
    <GridCellBase {...props} className={getBgColor(errors, warnings, infos)} disableToolTip>
      <div className="flex flex-nowrap items-center justify-between gap-1">
        <div
          data-tooltip-id={'global-tooltip'}
          data-tooltip-content={isUndefined(value) ? '' : value}
        >
          {isInvoiceEditDisabled ? (
            <span>{value}</span>
          ) : (
            <NumberInput
              value={isUndefined(value) ? '' : value}
              onChange={handleChange}
              valid={!errors?.length && !warnings?.length}
              onFocus={handleHover}
              ocr-data={`itemQuantity:${matchItemIndex}`}
            />
          )}
        </div>
        {showOcrIndicator && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default QuantityReceived;
